import { GenericMap } from '../../../utils/types';
import { CartItem } from '../../../utils/cart';
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  selectRestaurant,
  selectStage,
} from '../../../reducers/restaurantSlice';
import { selectMenuVersion } from '../../../reducers/menuSlice';
import { sendOrder } from '../../../reducers/orderSlice';
import { resetDialogSelectedItem } from '../../../reducers/dialogSlice';
import { cartActions } from '../../../reducers/cartSlice';

export interface IAutoComboState {
  autoCombinedCartItems: GenericMap<CartItem>; // Populate only when finding cart item match to form auto combo
  autoCombinedCartItemsQuantity: Record<string, number>;
  combinedCartItemIds: number[];
}

export const initialState: IAutoComboState = {
  autoCombinedCartItems: {},
  autoCombinedCartItemsQuantity: {},
  combinedCartItemIds: [],
};

const resetOriginalCart = (dispatch: Function) => {
  dispatch(resetDialogSelectedItem());
  dispatch(cartActions.clearCart());
  dispatch(resetCombinedCartItemds());
};

// Whenever cart-item is updated/added/deleted, refresh the autoCombinedCartItems in this slice
export const refreshMUAutoCombinedItems = createAsyncThunk(
  '',
  async (
    {
      menuUnificationOrderFailureCallback,
      menuUnificationOrderSuccessCallback,
    }: {
      menuUnificationOrderFailureCallback: Function;
      menuUnificationOrderSuccessCallback: Function;
    },
    { dispatch, getState }
  ) => {
    const {
      cart: { cartItems, cartItemsQuantity },
      autoCombo: {
        autoCombinedCartItems,
        combinedCartItemIds,
        autoCombinedCartItemsQuantity,
      },
    } = getState();

    if (Object.keys(autoCombinedCartItems).length) {
      // Loop through all cartItems
      Object.keys(cartItems).forEach((cartItemId: string) => {
        const nonAutoCombinedCartItem = autoCombinedCartItems[cartItemId];
        if (
          nonAutoCombinedCartItem ||
          !combinedCartItemIds.includes(Number(cartItemId))
        ) {
          // Update the non-autocombined item with latest cartItem (or) Add the non-autocombined item only if not present in any of earlier combined items
          dispatch(
            addRefreshNonComboItem({
              cartItemId,
              cartItem: cartItems[cartItemId],
              quantity: nonAutoCombinedCartItem
                ? autoCombinedCartItemsQuantity[cartItemId]
                : cartItemsQuantity[cartItemId],
            })
          );
        }
      });

      // Loop through all autoCombinedCartItems and remove if item not present in original cart
      Object.keys(autoCombinedCartItems).forEach((cartItemId: string) => {
        const cartItem = cartItems[cartItemId];
        const isAutoCombined =
          !!autoCombinedCartItems?.[cartItemId]?.isAutoCombined;
        const shouldDelete = !cartItem && !isAutoCombined;

        if (shouldDelete) {
          dispatch(
            cloneDeleteMUComboItem({
              cartItemId,
              operator: '-',
            })
          );
        }
      });

      if (
        !Object.values(getState().autoCombo.autoCombinedCartItemsQuantity)
          .length
      ) {
        resetOriginalCart(dispatch);
      }
    }

    dispatch(
      sendOrder({
        menuUnificationOrderFailureCallback,
        menuUnificationOrderSuccessCallback,
      })
    );
  }
);

const autoComboSlice = createSlice({
  name: 'autoCombo',
  initialState,
  reducers: {
    resetAutoComboState: () => {
      return initialState;
    },
    resetCombinedCartItemds: (state) => {
      state.combinedCartItemIds = [];
    },
    setAutoCombinedCart: (
      state,
      {
        payload: {
          autoCombinedCartItems,
          autoCombinedCartItemsQuantity,
          combinedCartItemIds,
        },
      }: PayloadAction<{
        autoCombinedCartItems: GenericMap<CartItem>;
        autoCombinedCartItemsQuantity: Record<string, number>;
        combinedCartItemIds?: number[];
      }>
    ) => {
      state.autoCombinedCartItems = autoCombinedCartItems;
      state.autoCombinedCartItemsQuantity = autoCombinedCartItemsQuantity;
      if (combinedCartItemIds) {
        state.combinedCartItemIds = combinedCartItemIds;
      }
    },
    addRefreshNonComboItem: (
      state,
      {
        payload: { cartItemId, cartItem, quantity },
      }: PayloadAction<{
        cartItemId: string;
        cartItem: CartItem;
        quantity: number;
      }>
    ) => {
      // Used for updating non combo cart-item into autocombinedCartItems
      state.autoCombinedCartItems[cartItemId] = cartItem;
      state.autoCombinedCartItemsQuantity[cartItemId] = quantity;
    },
    cloneDeleteMUComboItem: (
      state,
      {
        payload: { cartItemId, operator, sequenceId },
      }: PayloadAction<{
        cartItemId: string;
        operator: string;
        sequenceId?: number;
      }>
    ) => {
      // Used for brand menu orders (Clone / Delete)
      if (operator === '+' && sequenceId) {
        state.autoCombinedCartItems[sequenceId] = {
          ...state.autoCombinedCartItems[cartItemId],
          cartItemId: sequenceId,
        };
        state.autoCombinedCartItemsQuantity[sequenceId] =
          state.autoCombinedCartItemsQuantity[cartItemId];
      } else if (operator === '-') {
        delete state.autoCombinedCartItems[cartItemId];
        delete state.autoCombinedCartItemsQuantity[cartItemId];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        selectRestaurant.fulfilled,
        selectStage.fulfilled,
        selectMenuVersion.fulfilled
      ),
      () => {
        return initialState;
      }
    );
  },
});

const {
  setAutoCombinedCart,
  resetAutoComboState,
  cloneDeleteMUComboItem,
  addRefreshNonComboItem,
  resetCombinedCartItemds,
} = autoComboSlice.actions;

export {
  setAutoCombinedCart,
  resetAutoComboState,
  cloneDeleteMUComboItem,
  addRefreshNonComboItem,
  resetCombinedCartItemds,
};

export default autoComboSlice.reducer;
